<template>
  <div>
    <transition name="fade">
      <GuideVideoBanner
        v-if="showGuideCard && text"
        :text="text"
        :subtext="subText"
        :videoUrl="videoUrl"
        :videoThumbnail="videoThumbnail"
        @close="handleGuideClose"
      />
    </transition>
    <div class="your-badges-tab">
      <YourBadgesTable
        :data-stream="employeeBadges"
        @chatter-click="handleChatterClick"
      />
    </div>
    <ChatterDrawer
      :drawer="showChatterDrawer"
      type="Badge"
      :typeValue="selectedBadgeChatter?.badgeLevelName || ''"
      :listData="selectedBadgeChatter"
      @closeDrawer="closeChatterDrawer"
      iconName="org_icon_responsibility"
      :chatterList="chatterData"
      :totalCheckpointChatter="totalChatter"
      @requestMoreCheckpointsChatter="loadMoreChatter"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, getCurrentInstance, watch } from "vue";
import GuideVideoBanner from "@components/GuideVideoBanner/GuideVideoBanner.vue";
import YourBadgesTable from "@components/YourBadgesTable/YourBadgesTable.vue";
import ChatterDrawer from "@components/Drawer/ChatterDrawer.vue";
import { getEmployeeBadges, getChatterList } from "@/api";

const { proxy } = getCurrentInstance();

const props = defineProps({
  dataStream: {
    type: Array,
    default: () => [],
  },
  text: {
    type: String,
    default: "",
  },
  subText: {
    type: String,
    default: "",
  },
  videoUrl: {
    type: String,
    default: "",
  },
  videoThumbnail: {
    type: String,
    default: "",
  },
  showGuideCard: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['guide-close']);

const employeeBadges = ref([]);
const showChatterDrawer = ref(false);
const selectedBadgeChatter = ref(null);
const chatterData = ref([]);
const chatterPage = ref(1);
const totalChatter = ref(0);
const chatterLoading = ref(false);

const handleGuideClose = () => {
  emit('guide-close');
};
onMounted(() => {
  const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
  const authUser = JSON.parse(localStorage.getItem("user"));
  const empId = authUser?.employee?.id;
  fetchEmployeeBadges(accountID, empId);
});
watch(
  () => props.dataStream,
  (newVal) => {
    if (newVal.length > 0) {
      employeeBadges.value = newVal;
    }
  },
  {
    immediate: true,
    deep: true,
  }
);
async function fetchEmployeeBadges(accountID, empId) {
  proxy.$loader.show();
  try {
    const { data } = await getEmployeeBadges(accountID, empId);
    if (data) {
      employeeBadges.value = data.results.map((item) => ({
        id: item.id,
        is_current: item.is_current,
        issued_to: item.issued_to,
        issued_by: item.issued_by,
        issued_on: item.issued_on,
        access: item.access,
        removed_on: item.removed_on,
        currentBadge: {
          level: item.level || "",
          dot: getBadgeDotFromStatus(item.status),
          view: item.remove_method,
          expiration_date: item.expiration_date,
          has_expiration_if_obsolete: item.expires_when_obsolete,
          is_current: item.is_current,
        },
        responsibility: {
          code: item.issued_for.code,
          name: item.issued_for.name,
        },
        removedBy: item.removed_by
          ? {
              name: item.removed_by?.name,
              picture: item.removed_by?.picture || "",
              userType: "user",
              date: item?.removed_on || "",
            }
          : null,
        grantedBy: item.awarded_by
          ? {
              name: item.awarded_by?.name,
              picture: item.awarded_by?.picture || "",
              userType: "user",
              date: item?.awarded_on || "",
            }
          : null,
        reason: item.remove_reason === "string" ? "" : item.remove_reason,
        status: item.status,
        remove_method: item.remove_method,
      }));
    }
  } catch (error) {
    console.error("Error fetching employee badges:", error);
  } finally {
    proxy.$loader.hide();
  }
}
function getBadgeDotFromStatus(status) {
  switch (status) {
    case "given":
      return "Given";
    default:
      return "Good Standing";
  }
}

async function handleChatterClick(badgeData) {
  if (!badgeData || !badgeData.id) {
    console.warn("Invalid badge data for chatter:", badgeData);
    return;
  }

  // Get badge level name
  const badgeLevel = badgeData.currentBadge?.level || "";
  const badgeLevelName =
    badgeLevel.charAt(0).toUpperCase() + badgeLevel.slice(1);

  // Store badge info for drawer
  selectedBadgeChatter.value = {
    id: badgeData.id,
    badgeLevelName: badgeLevelName,
    level: badgeLevel,
    responsibility: badgeData.responsibility,
  };

  // Reset chatter data when opening drawer
  chatterData.value = [];
  chatterPage.value = 1;
  totalChatter.value = 0;
  showChatterDrawer.value = true;

  // Fetch chatter data
  await fetchChatter();
}

function closeChatterDrawer() {
  showChatterDrawer.value = false;
  selectedBadgeChatter.value = null;
  chatterData.value = [];
  chatterPage.value = 1;
  totalChatter.value = 0;
}

async function fetchChatter() {
  if (
    chatterLoading.value ||
    !selectedBadgeChatter.value ||
    !selectedBadgeChatter.value.id
  )
    return;

  chatterLoading.value = true;
  try {
    const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
    const badgeId = selectedBadgeChatter.value.id;

    const { data } = await getChatterList(accountID, badgeId, "badge", chatterPage.value);

    if (data && data.count) {
      totalChatter.value = data.count;
    }

    if (data && data.results) {
      const chatterItems = data.results.map((el) => {
        const date = new Date(el.recorded_on);
        const milliseconds = date.getTime();
        const millisecondsString = milliseconds.toString();
        const nameParts = el.recorded_by_name
          ? el.recorded_by_name.split(" ")
          : [""];

        const newEl = {
          id: el.id,
          IsExIQtiveBot: false,
          message: el.note,
          created_on: millisecondsString,
          IsHistory: el.is_history,
        };

        if (nameParts.length >= 2) {
          newEl.avatar = {
            picture: "",
            firstName: nameParts[0],
            lastName: nameParts.slice(1).join(" "),
          };
        } else {
          newEl.avatar = {
            picture: "",
            firstName: el.recorded_by_name || "",
            lastName: "",
          };
        }

        return newEl;
      });

      chatterData.value.push(...chatterItems);
    }
  } catch (error) {
    console.error("Error fetching chatter:", error);
  } finally {
    chatterLoading.value = false;
  }
}

async function loadMoreChatter() {
  if (chatterLoading.value) return;
  chatterPage.value += 1;
  await fetchChatter();
}
</script>
<style>
.your-badges-tab {
  margin-top: 15px;
}

/* Fade transition styles */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>
